import { Identifiable } from '../interface/identifiable';
import { Factory } from '../interface/factory';
import { CountryCode } from '../enum/country-code';
import { I18nString } from './i18nstring';
import { HasCountryCode } from '../interface/has-country-code';
import { Period } from '../enum/period';
import { Modifiable } from '../interface/modifiable';
import { DiscountEligibility } from './discount-eligibility';
import { TrialPeriodLength } from './trial/trial-period-length';
import { Deletable } from '..';
import { OwnerRef } from './owner-ref';

export enum DiscountType {
  PercentageVoucher = 'PercentageVoucher',
  SinglePeriodPercentageVoucher = 'SinglePeriodPercentageVoucher',
  SinglePeriodPriceCampaign = 'SinglePeriodPriceCampaign',
  TrialVoucher = 'TrialVoucher',
  PersonalTrialVoucher = 'PersonalTrialVoucher',
}

export class Discount implements Identifiable, Deletable {
  type: DiscountType;

  id: string;
  countryCode: CountryCode;
  owner: OwnerRef;
  expiry?: Date;
  created: Date;
  modified: Date;
  deleted?: boolean;

  constructor(json: any, type: DiscountType) {
    this.type = type;
    this.id = json.id;
    this.countryCode = json.countryCode as CountryCode;
    const of: Factory<OwnerRef> = OwnerRef.getFactory();
    this.owner = of.make(json.owner);
    this.expiry = json.expiry ? new Date(json.expiry) : undefined;
    this.created = new Date(json.created);
    this.modified = new Date(json.modified);
    this.deleted = json.deleted ? Boolean(json.deleted) : false;
  }

  public static getFactory(): Factory<Discount> {
    return new (class implements Factory<Discount> {
      make(json: any): Discount {
        switch (json.type) {
          case DiscountType.PercentageVoucher:
            return new DiscountPercentageVoucher(json);
          case DiscountType.SinglePeriodPercentageVoucher:
            return new DiscountSinglePeriodPercentageVoucher(json);
          case DiscountType.SinglePeriodPriceCampaign:
            return new DiscountSinglePeriodPriceCampaign(json);
          case DiscountType.TrialVoucher:
            return new TrialVoucherDiscount(json);
          case DiscountType.PersonalTrialVoucher:
            return new PersonalTrialVoucherDiscount(json);
          default:
            throw new Error('Unrecognized Discount type (' + json.type + ').');
        }
      }

      getTableName(): string {
        return 'discounts';
      }
    })();
  }

  static getUrl(countryCode: CountryCode, discountId?: string): string;
  static getUrl(countryCode: CountryCode, discountId: string): string {
    return '/countries/' + countryCode + '/discounts' + (discountId ? '/' + discountId : '');
  }
}

// Claimable, redeemable and applies for all payments until expiry.
export class DiscountPercentageVoucher extends Discount implements Identifiable, HasCountryCode {
  templateId?: string;
  code: string; // NOTE: When a template is deleted, the code is set to template id.

  eligibility?: DiscountEligibility[];
  percent: string;
  titles?: I18nString[];
  texts?: I18nString[];

  constructor(json: any) {
    super(json, DiscountType.PercentageVoucher);

    this.templateId = json.templateId ?? undefined;
    this.code = json.code;

    const ef: Factory<DiscountEligibility> = DiscountEligibility.getFactory();
    this.eligibility = json.eligibility ? json.eligibility.map((js: any) => ef.make(js)) : undefined;
    this.percent = json.percent;
    this.titles = json.titles ? json.titles.map((js: any) => new I18nString(js)) : undefined;
    this.texts = json.texts ? json.texts.map((js: any) => new I18nString(js)) : undefined;
  }
}

// Claimable and redeemable until expiry. Applies for payments in one period only.
export class DiscountSinglePeriodPercentageVoucher extends Discount implements Identifiable, HasCountryCode {
  templateId?: string;
  code: string; // NOTE: When a template is deleted, the code is set to template id.

  eligibility?: DiscountEligibility[];
  percent: string;
  titles?: I18nString[];
  texts?: I18nString[];
  period: Period;

  constructor(json: any) {
    super(json, DiscountType.SinglePeriodPercentageVoucher);

    this.templateId = json.templateId ?? undefined;
    this.code = json.code;

    const ef: Factory<DiscountEligibility> = DiscountEligibility.getFactory();
    this.eligibility = json.eligibility ? json.eligibility.map((js: any) => ef.make(js)) : undefined;
    this.percent = json.percent;
    this.titles = json.titles ? json.titles.map((js: any) => new I18nString(js)) : undefined;
    this.texts = json.texts ? json.texts.map((js: any) => new I18nString(js)) : undefined;
    this.period = json.period as Period;
  }
}

// For use in product template costs.
// NOTE: Redeemable until expiry.
export class DiscountSinglePeriodPriceCampaign extends Discount implements Identifiable, Modifiable {
  productId: string;
  price: string;
  period: Date;

  constructor(json: any) {
    super(json, DiscountType.SinglePeriodPriceCampaign);

    this.productId = json.productId ?? undefined;
    this.price = json.price;
    this.period = new Date(json.period);
  }
}

export class TrialVoucherDiscount extends Discount implements Identifiable {
  templateId?: string;
  code: string;
  trialPeriod: TrialPeriodLength;
  status: RedemptionStatus;
  eligibility?: DiscountEligibility[];

  constructor(json: any) {
    super(json, DiscountType.TrialVoucher);
    this.templateId = json.templateId ?? undefined;
    this.code = json.code;
    this.trialPeriod = json.trialPeriod;
    this.status = RedemptionStatus.getFactory().make(json.status);
    const ef: Factory<DiscountEligibility> = DiscountEligibility.getFactory();
    this.eligibility = json.eligibility ? json.eligibility.map((js: any) => ef.make(js)) : undefined;
  }
}

export class PersonalTrialVoucherDiscount extends Discount implements Identifiable {
  trialPeriod: TrialPeriodLength;
  status: RedemptionStatus;
  eligibility?: DiscountEligibility[];

  constructor(json: any) {
    super(json, DiscountType.PersonalTrialVoucher);
    this.trialPeriod = json.trialPeriod;
    this.status = RedemptionStatus.getFactory().make(json.status);
    this.eligibility = json.eligibility
      ? json.eligibility.map((js: any) => DiscountEligibility.getFactory().make(js))
      : undefined;
  }
}

export enum RedemptionStatusType {
  Redeemed = 'Redeemed',
  Available = 'Available',
}

export class RedemptionStatus {
  type: RedemptionStatusType;
  constructor(json: any) {
    this.type = json.type as RedemptionStatusType;
  }

  public static getFactory(): Factory<RedemptionStatus> {
    return new (class implements Factory<RedemptionStatus> {
      make(json: any): RedemptionStatus {
        switch (json.type) {
          case RedemptionStatusType.Redeemed:
            return new RedeemedRedemptionStatus(json);
          case RedemptionStatusType.Available:
            return new AvailableRedemptionStatus(json);
          default:
            throw new Error('Unrecognized RedemptionStatus type (' + json.type + ').');
        }
      }

      getTableName(): string {
        throw new Error('Redemption statuses are never stored in IndexedDB.');
      }
    })();
  }
}

export class RedeemedRedemptionStatus extends RedemptionStatus {
  productId: string;
  constructor(json: any) {
    super(json);
    this.productId = json.productId;
  }
}

export class AvailableRedemptionStatus extends RedemptionStatus {
  constructor(json: any) {
    super(json);
  }
}
